/**
 * useAnnouncements Hook
 * 
 * React hook for managing announcements with real-time updates
 */

import { useState, useEffect, useCallback } from 'react';
import { AnnouncementService } from '../services/AnnouncementService';
import { Announcement, AnnouncementFilters, AnnouncementData } from '../@types/announcement';
import { useAuth } from '../contexts/AuthContext';
import { Unsubscribe } from 'firebase/firestore';

interface UseAnnouncementsReturn {
  announcements: Announcement[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  fetchAnnouncements: (filters?: AnnouncementFilters) => Promise<void>;
  createAnnouncement: (data: AnnouncementData) => Promise<string>;
  updateAnnouncement: (id: string, data: Partial<AnnouncementData>) => Promise<void>;
  deleteAnnouncement: (id: string) => Promise<void>;
  archiveAnnouncement: (id: string) => Promise<void>;
  recordView: (announcementId: string) => Promise<void>;
  refreshAnnouncements: () => Promise<void>;
}

export const useAnnouncements = (filters?: AnnouncementFilters): UseAnnouncementsReturn => {
  const { user, userRole } = useAuth();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnnouncements = useCallback(async (customFilters?: AnnouncementFilters) => {
    if (!user || !userRole) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use custom filters if provided, otherwise use default filters
      const finalFilters = customFilters || filters;

      // If manager, get all announcements; otherwise get user-specific announcements
      const fetchedAnnouncements = userRole === 'manager'
        ? await AnnouncementService.getAllAnnouncements(finalFilters)
        : await AnnouncementService.getUserAnnouncements(user.id, userRole, finalFilters);

      setAnnouncements(fetchedAnnouncements || []);

      // Update unread count (only for non-managers viewing their own announcements)
      if (userRole !== 'manager') {
        try {
          const count = await AnnouncementService.getUnreadCount(user.id, userRole);
          setUnreadCount(count);
        } catch (countError) {
          console.warn('Could not fetch unread count:', countError);
          setUnreadCount(0);
        }
      } else {
        setUnreadCount(0); // Managers don't have unread counts
      }
    } catch (err: any) {
      console.error('Error fetching announcements:', err);
      setError(err.message || 'Failed to fetch announcements');
      setAnnouncements([]); // Set empty array on error
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [user, userRole, filters]);

  const createAnnouncement = useCallback(async (data: AnnouncementData): Promise<string> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const userName = user.username || user.email || 'Unknown';
      const announcementId = await AnnouncementService.createAnnouncement(
        data,
        user.id,
        userName
      );
      await fetchAnnouncements();
      return announcementId;
    } catch (err: any) {
      console.error('Error creating announcement:', err);
      throw err;
    }
  }, [user, fetchAnnouncements]);

  const updateAnnouncement = useCallback(async (
    id: string,
    data: Partial<AnnouncementData>
  ): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      await AnnouncementService.updateAnnouncement(id, data, user.id);
      await fetchAnnouncements();
    } catch (err: any) {
      console.error('Error updating announcement:', err);
      throw err;
    }
  }, [user, fetchAnnouncements]);

  const deleteAnnouncement = useCallback(async (id: string): Promise<void> => {
    try {
      await AnnouncementService.deleteAnnouncement(id);
      await fetchAnnouncements();
    } catch (err: any) {
      console.error('Error deleting announcement:', err);
      throw err;
    }
  }, [fetchAnnouncements]);

  const archiveAnnouncement = useCallback(async (id: string): Promise<void> => {
    try {
      await AnnouncementService.archiveAnnouncement(id);
      await fetchAnnouncements();
    } catch (err: any) {
      console.error('Error archiving announcement:', err);
      throw err;
    }
  }, [fetchAnnouncements]);

  const recordView = useCallback(async (announcementId: string): Promise<void> => {
    if (!user) {
      return;
    }

    try {
      await AnnouncementService.recordView(announcementId, user.id);
      
      // Update local state to mark as viewed
      setAnnouncements(prev => prev.map(announcement => {
        if (announcement.id === announcementId) {
          // Check if already viewed by this user
          const alreadyViewed = announcement.views.some(view => view.userId === user.id);
          if (!alreadyViewed) {
            return {
              ...announcement,
              views: [...announcement.views, {
                userId: user.id,
                viewedAt: { toDate: () => new Date() } as any
              }]
            };
          }
        }
        return announcement;
      }));

      // Update unread count
      if (userRole && userRole !== 'manager') {
        const count = await AnnouncementService.getUnreadCount(user.id, userRole);
        setUnreadCount(count);
      }
    } catch (err: any) {
      console.error('Error recording view:', err);
      // Don't throw - view recording failure shouldn't break functionality
    }
  }, [user, userRole]);

  const refreshAnnouncements = useCallback(async () => {
    await fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Initial fetch
  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  // Real-time subscription
  useEffect(() => {
    if (!user || !userRole || userRole === 'manager') {
      return;
    }

    const unsubscribe = AnnouncementService.subscribeToAnnouncements(
      user.id,
      userRole,
      (updatedAnnouncements) => {
        setAnnouncements(updatedAnnouncements);
        
        // Update unread count
        AnnouncementService.getUnreadCount(user.id, userRole)
          .then(count => setUnreadCount(count))
          .catch(console.error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user, userRole]);

  return {
    announcements,
    unreadCount,
    loading,
    error,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    archiveAnnouncement,
    recordView,
    refreshAnnouncements
  };
};

